import React, { useState, useEffect, useRef } from "react";
import styles from "./CallSetting.module.css";
import axios from "axios";
import { API_BASE_URL, token, updateAgentFromBackend } from "../../Store/apiStore";

// What each kind of agent says to a phone's call screener (iPhone Call
// Screening, Google Call Screen), and the {{variables}} it can use.
//   transfer  receptionist: the OWNER's phone screens a transfer
//   outbound  Pounce: the LEAD's phone screens our call
const VARIANTS = {
  transfer: {
    description: "Enable a predefined message for when call screening picks up a transfer.",
    identity: "e.g. ABC Dental reception",
    purpose: "e.g. transferring {{caller_name}} who needs help with {{reason}}",
    variables: ["caller_name", "caller_number", "reason", "department", "agent_name", "business_name"],
  },
  outbound: {
    description: "Enable a predefined message for when call screening picks up.",
    identity: "e.g. ABC Dental scheduling team",
    purpose: "e.g. following up on {{lead_first_name}}'s enquiry",
    variables: ["lead_first_name", "lead_name", "lead_inquiry", "business_name"],
  },
};

// iOS / Android Call Screen Handling — Python agents only (Retell agents keep
// Retell's own setting). Saves itself, like the other Call Setting controls.
// `className` is the page's own card, so nothing is drawn for a Retell agent.
export default function CallScreenSetting({ agentId, variant = "transfer", className, style }) {
  const v = VARIANTS[variant] || VARIANTS.transfer;
  const [isPython, setIsPython] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [identity, setIdentity] = useState("");
  const [purpose, setPurpose] = useState("");

  const resolvedAgentId = agentId || sessionStorage.getItem("SelectAgentId");

  useEffect(() => {
    if (!resolvedAgentId) return;
    const fetchAgentDetails = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/agent/getAgent/${resolvedAgentId}`, {
          headers: {
            "Content-Type": "application/json",
            // The current token, not apiStore's copy from page load (see TransferSetting).
            Authorization: `Bearer ${localStorage.getItem("token") || token}`,
          },
        });
        const { agentCreationType, agentRole, call_screen_enabled, call_screen_identity, call_screen_purpose } =
          res.data;
        // The transfer variant means nothing on a Pounce agent (no transfer on
        // its Twilio calls) — its own Calling Settings shows the outbound one.
        const pounce = String(agentRole || "").toLowerCase() === "pounce";
        setIsPython(
          String(agentCreationType || "").toLowerCase() === "python" && (variant !== "transfer" || !pounce)
        );
        setEnabled(Boolean(call_screen_enabled));
        setIdentity(call_screen_identity || "");
        setPurpose(call_screen_purpose || "");
      } catch (error) {
        console.error("Error fetching agent details:", error);
      }
    };
    fetchAgentDetails();
  }, [resolvedAgentId, variant]);

  // DEBOUNCED per field, like the sliders.
  const commitTimers = useRef({});
  const commit = (field, value) => {
    if (!resolvedAgentId) return;
    clearTimeout(commitTimers.current[field]);
    commitTimers.current[field] = setTimeout(async () => {
      try {
        await updateAgentFromBackend({ agent_id: resolvedAgentId, [field]: value });
      } catch (error) {
        console.error(`Failed to update ${field}:`, error);
      }
    }, 400);
  };

  if (!isPython) return null;

  return (
    <div className={className} style={style}>
      <div className={styles.toggleRow}>
        <label className={styles.label}>iOS / Android Call Screen Handling</label>
        <label className={styles.switch}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={() => {
              setEnabled(!enabled);
              commit("call_screen_enabled", !enabled);
            }}
          />
          <span className={styles.slider}></span>
        </label>
      </div>
      <p className={styles.description}>{v.description}</p>

      {enabled && (
        <div className={styles.vmBox}>
          <p className={styles.vmTitle}>Agent identity</p>
          <input
            className={styles.csInput}
            maxLength={100}
            value={identity}
            placeholder={v.identity}
            onChange={(e) => {
              setIdentity(e.target.value);
              commit("call_screen_identity", e.target.value);
            }}
          />
          <p className={`${styles.vmTitle} ${styles.csGap}`}>Purpose of the call</p>
          <textarea
            className={styles.vmText}
            rows={3}
            maxLength={300}
            value={purpose}
            placeholder={v.purpose}
            onChange={(e) => {
              setPurpose(e.target.value);
              commit("call_screen_purpose", e.target.value);
            }}
          />
          <p className={styles.vmHint}>
            Said as: "Hi, this is [identity], calling about [purpose]." Variables:{" "}
            {v.variables.map((name) => `{{${name}}}`).join(", ")}
          </p>
        </div>
      )}
    </div>
  );
}
